/**
 * Historial de conversaciones del chat con IA (Guía del portal, Analista de tickets, Agente CEO):
 * agrupado por fecha, con búsqueda y acciones por conversación (repetir la pregunta en una
 * conversación nueva, renombrar, fijar, eliminar con confirmación). Sin dominio: recibe las listas
 * y avisa qué eligió el usuario; los datos y las escrituras viven en el hook de cada agente.
 *
 * Secciones opcionales: "Compartidas contigo" (de otra persona: se abren y se repiten, no se
 * editan) y "Archivadas". Cada agente puede sumar acciones propias al menú (`accionesExtra`).
 */
import { useMemo, useState, type KeyboardEvent, type ReactNode } from "react";
import { EmptyState } from "@/components/ui/data-states";
import { MessagesSquare, MoreHorizontal, Pencil, Pin, PinOff, Repeat2, RotateCcw, Search, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  MAX_TITULO,
  agruparConversaciones,
  cuandoConversacion,
  filtrarConversaciones,
  tituloNuevo,
  type ConversacionIa,
} from "@/lib/chatIa";

export interface ChatIaHistorialProps<T extends ConversacionIa> {
  /** Mis conversaciones vigentes: se agrupan por fecha. */
  conversaciones: T[];
  /** De otra persona, compartidas conmigo (solo lectura). */
  compartidas?: T[];
  archivadas?: T[];
  cargando: boolean;
  error: boolean;
  /** `id` de la conversación que está en el chat (para marcarla). */
  activa: string | null;
  /** Conversación que se está abriendo (muestra su fila ocupada). */
  abriendo: string | null;
  /** Deshabilita "Repetir" mientras el asistente responde o está bloqueado por límite. */
  puedePreguntar: boolean;
  onAbrir: (c: T) => void;
  onRepetir: (c: T) => void;
  onRenombrar: (c: T, titulo: string) => void;
  onFijar: (c: T, fijada: boolean) => void;
  onEliminar: (c: T) => void;
  onReintentar: () => void;
  /** Ítems extra del menú de una conversación propia (`DropdownMenuItem`). */
  accionesExtra?: (c: T) => ReactNode;
  /** Explicación del diálogo de eliminar. */
  textoEliminar?: string;
  /** Reemplaza el estado vacío (p. ej. el agente aún no guarda conversaciones). */
  aviso?: ReactNode;
  className?: string;
}

export function ChatIaHistorial<T extends ConversacionIa>({
  conversaciones,
  compartidas = [],
  archivadas = [],
  cargando,
  error,
  activa,
  abriendo,
  puedePreguntar,
  onAbrir,
  onRepetir,
  onRenombrar,
  onFijar,
  onEliminar,
  onReintentar,
  accionesExtra,
  textoEliminar = "desaparece de tu historial y ya no se puede continuar ni repetir.",
  aviso,
  className,
}: ChatIaHistorialProps<T>) {
  const [busqueda, setBusqueda] = useState("");
  const [editando, setEditando] = useState<string | null>(null);
  const [porEliminar, setPorEliminar] = useState<T | null>(null);

  const grupos = useMemo(() => {
    const ahora = new Date();
    const extra = [
      { titulo: "Compartidas contigo", conversaciones: filtrarConversaciones(compartidas, busqueda) },
      { titulo: "Archivadas", conversaciones: filtrarConversaciones(archivadas, busqueda) },
    ].filter((g) => g.conversaciones.length > 0);
    return [...agruparConversaciones(filtrarConversaciones(conversaciones, busqueda), ahora), ...extra];
  }, [conversaciones, compartidas, archivadas, busqueda]);
  const total = conversaciones.length + compartidas.length + archivadas.length;
  const ahora = new Date();

  return (
    <aside className={cn("flex min-h-0 w-full flex-col bg-muted/30", className)} aria-label="Historial de conversaciones">
      <div className="space-y-2.5 px-3 pb-2 pt-3">
        <div className="flex items-baseline justify-between px-1">
          <h3 className="text-[13px] font-semibold text-foreground">Historial</h3>
          {total > 0 && <span className="text-[11px] tabular-nums text-muted-foreground">{total}</span>}
        </div>
        {total > 0 && (
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar conversación"
              aria-label="Buscar en el historial"
              className="h-8 rounded-lg bg-background pl-8 text-[12.5px]"
            />
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {cargando ? (
          <div className="space-y-2 px-1 pt-1" role="status" aria-label="Cargando historial">
            {[70, 90, 60, 80].map((w) => (
              <Skeleton key={w} className="h-9 rounded-lg" style={{ width: `${w}%` }} />
            ))}
          </div>
        ) : error ? (
          <div className="space-y-2 px-2 pt-4 text-center text-[12px] text-muted-foreground">
            <p>No se pudo cargar el historial.</p>
            <Button type="button" variant="outline" size="sm" onClick={onReintentar} className="h-7 px-2.5 text-[12px]">
              <RotateCcw />
              Reintentar
            </Button>
          </div>
        ) : total === 0 ? (
          aviso ? (
            <div className="px-3 pt-6 text-center">{aviso}</div>
          ) : (
            <EmptyState
              compacto
              icono={MessagesSquare}
              titulo="Aún no hay conversaciones"
              texto="Lo que preguntes se guarda aquí para repetirlo cuando lo necesites."
            />
          )
        ) : grupos.length === 0 ? (
          <EmptyState compacto titulo={`Sin resultados para "${busqueda.trim()}"`} />
        ) : (
          grupos.map((g) => (
            <section key={g.titulo} className="mt-2 first:mt-0">
              <h4 className="px-2 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                {g.titulo}
              </h4>
              <ul className="space-y-0.5">
                {g.conversaciones.map((c) => (
                  <li key={c.id}>
                    {editando === c.id ? (
                      <TituloEditable
                        inicial={c.titulo}
                        onGuardar={(valor) => {
                          setEditando(null);
                          const t = tituloNuevo(valor, c.titulo);
                          if (t) onRenombrar(c, t);
                        }}
                        onCancelar={() => setEditando(null)}
                      />
                    ) : (
                      <FilaConversacion
                        conversacion={c}
                        cuando={c.detalle || cuandoConversacion(c.fecha, ahora)}
                        activa={activa === c.id}
                        ocupada={abriendo === c.id}
                        puedePreguntar={puedePreguntar}
                        onAbrir={() => onAbrir(c)}
                        onRepetir={() => onRepetir(c)}
                        onRenombrar={() => setEditando(c.id)}
                        onFijar={() => onFijar(c, !c.fijada)}
                        onEliminar={() => setPorEliminar(c)}
                        extra={c.propia !== false ? accionesExtra?.(c) : null}
                      />
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </div>

      <AlertDialog open={porEliminar !== null} onOpenChange={(o) => !o && setPorEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar conversación</AlertDialogTitle>
            <AlertDialogDescription>
              "{porEliminar?.titulo}" {textoEliminar}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (porEliminar) onEliminar(porEliminar);
                setPorEliminar(null);
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
}

function FilaConversacion({
  conversacion: c,
  cuando,
  activa,
  ocupada,
  puedePreguntar,
  onAbrir,
  onRepetir,
  onRenombrar,
  onFijar,
  onEliminar,
  extra,
}: {
  conversacion: ConversacionIa;
  cuando: string;
  activa: boolean;
  ocupada: boolean;
  puedePreguntar: boolean;
  onAbrir: () => void;
  onRepetir: () => void;
  onRenombrar: () => void;
  onFijar: () => void;
  onEliminar: () => void;
  extra?: ReactNode;
}) {
  const propia = c.propia !== false;
  return (
    <div
      className={cn(
        "group/fila relative flex items-center rounded-lg transition-colors",
        activa ? "bg-primary/10" : "hover:bg-background",
        ocupada && "opacity-60",
      )}
    >
      {activa && <span aria-hidden className="absolute inset-y-1.5 left-0 w-[3px] rounded-full bg-primary" />}
      <button
        type="button"
        onClick={onAbrir}
        disabled={ocupada}
        aria-current={activa ? "true" : undefined}
        className="min-w-0 flex-1 rounded-lg py-2 pl-3 pr-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="flex items-center gap-1.5">
          {c.fijada && <Pin className="size-3 shrink-0 text-primary" aria-label="Fijada" />}
          <span className={cn("truncate text-[12.5px]", activa ? "font-semibold text-foreground" : "text-foreground/90")}>
            {c.titulo}
          </span>
        </span>
        <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">{cuando}</span>
      </button>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Opciones de "${c.titulo}"`}
            className={cn(
              "mr-1 inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-opacity",
              "hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              "opacity-100 md:opacity-0 md:group-hover/fila:opacity-100 data-[state=open]:opacity-100",
            )}
          >
            <MoreHorizontal className="size-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem disabled={!puedePreguntar} onSelect={onRepetir} className="gap-2 text-[13px]">
            <Repeat2 className="size-4 text-primary" />
            Repetir pregunta
          </DropdownMenuItem>
          {propia && (
            <>
              <DropdownMenuItem onSelect={onRenombrar} className="gap-2 text-[13px]">
                <Pencil className="size-4" />
                Renombrar
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onFijar} className="gap-2 text-[13px]">
                {c.fijada ? <PinOff className="size-4" /> : <Pin className="size-4" />}
                {c.fijada ? "Quitar de fijadas" : "Fijar arriba"}
              </DropdownMenuItem>
              {extra}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={onEliminar}
                className="gap-2 text-[13px] text-destructive focus:bg-destructive/10 focus:text-destructive"
              >
                <Trash2 className="size-4" />
                Eliminar
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function TituloEditable({
  inicial,
  onGuardar,
  onCancelar,
}: {
  inicial: string;
  onGuardar: (titulo: string) => void;
  onCancelar: () => void;
}) {
  const [valor, setValor] = useState(inicial);
  const alTeclear = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onGuardar(valor);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onCancelar();
    }
  };
  return (
    <Input
      autoFocus
      value={valor}
      maxLength={MAX_TITULO}
      onChange={(e) => setValor(e.target.value)}
      onKeyDown={alTeclear}
      onBlur={() => onGuardar(valor)}
      aria-label="Nuevo nombre de la conversación"
      className="h-9 rounded-lg bg-background text-[12.5px]"
    />
  );
}
